import type {
  InsuranceNetwork,
  TherapeuticChoiceCombination,
  TherapeuticChoiceRowKey,
  TherapeuticChoiceRowSettings,
} from "cadendar-shared";
import {
  getTherapeuticMaterialOptionByCode,
  therapeuticActKindLabels,
  therapeuticChoiceRowLabels,
  therapeuticChoiceRowOrder,
} from "cadendar-shared";
import Button from "../../main/components/Button";

interface TherapeuticChoicesTableProps {
  title: string;
  network: InsuranceNetwork | null;
  rowSettings: TherapeuticChoiceRowSettings;
  onAddChoice: (rowKey: TherapeuticChoiceRowKey) => void;
  onEditChoice: (rowKey: TherapeuticChoiceRowKey, index: number) => void;
  onDeleteChoice: (rowKey: TherapeuticChoiceRowKey, index: number) => void;
}

function renderChoiceSummary(choice: TherapeuticChoiceCombination) {
  return choice.acts.map((act) => {
    const materialLabel =
      act.materialCode !== null
        ? getTherapeuticMaterialOptionByCode(act.materialCode)?.selectLabel ??
          act.materialCode
        : null;

    return (
      <div key={act.id} className="rounded-lg bg-slate-50 px-2 py-1 text-xs text-slate-700">
        <div className="font-medium text-slate-900">
          {therapeuticActKindLabels[act.kind]}
        </div>
        <div className="font-mono text-slate-500">{act.ccamCode}</div>
        {materialLabel ? <div>{materialLabel}</div> : null}
      </div>
    );
  });
}

const TherapeuticChoicesTable = ({
  title,
  rowSettings,
  onAddChoice,
  onEditChoice,
  onDeleteChoice,
}: TherapeuticChoicesTableProps) => {
  return (
    <section className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <h2 className="text-lg font-semibold text-slate-950">{title}</h2>

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="bg-slate-100 text-left text-slate-700">
              <th className="w-[280px] border border-slate-200 px-4 py-3 font-semibold">
                Ligne
              </th>
              <th className="border border-slate-200 px-4 py-3 font-semibold">
                Choix 1
              </th>
              <th className="border border-slate-200 px-4 py-3 font-semibold">
                Choix 2
              </th>
              <th className="border border-slate-200 px-4 py-3 font-semibold">
                Choix 3
              </th>
            </tr>
          </thead>
          <tbody>
            {therapeuticChoiceRowOrder.map((rowKey) => {
              const row = rowSettings[rowKey];

              return (
                <tr key={rowKey} className="align-top">
                  <td className="border border-slate-200 px-4 py-3 font-medium text-slate-900">
                    {therapeuticChoiceRowLabels[rowKey]}
                  </td>

                  {[0, 1, 2].map((choiceIndex) => {
                    const choice = row[choiceIndex];

                    return (
                      <td key={choiceIndex} className="border border-slate-200 px-4 py-3">
                        {choice ? (
                          <div className="space-y-3">
                            <div className="space-y-2">{renderChoiceSummary(choice)}</div>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                title="Modifier"
                                onClick={() => onEditChoice(rowKey, choiceIndex)}
                                style="secondary"
                                size="small"
                              />
                              <Button
                                title="Supprimer"
                                onClick={() => onDeleteChoice(rowKey, choiceIndex)}
                                style="danger"
                                size="small"
                              />
                            </div>
                          </div>
                        ) : row.length < 3 ? (
                          <div className="flex min-h-[120px] items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50">
                            <Button
                              title="Ajouter un choix"
                              onClick={() => onAddChoice(rowKey)}
                              style="secondary"
                            />
                          </div>
                        ) : (
                          <div className="text-sm text-slate-400">Aucun emplacement libre</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default TherapeuticChoicesTable;
